import React, {useCallback, useEffect, useRef, useState} from 'react';
import GridHeader from "../components/GridHeader";
import {
    createRepresentations,
    deleteRepresentations,
    getRepresentations
} from "../pages/reports/components/representation/api";
import Representation from "../pages/reports/components/representation/representation";
import {getColumns, getList} from "./api";
import {toast} from "react-toastify";
import ParcelsButtons from "./parcelsButtons";
import TableGrid from "../components/Table/TableGrid";
import {getFilterTypeByName, sortIsOn} from "./utils";
import {apiGet, apiPost} from "../services/apiClient";
import {getRoles} from '../services/userRights';
import debounce from "lodash.debounce";
import {Button, Dimmer, Segment} from "semantic-ui-react";
import Loader from "../components/Loader/Loader";
import DictionaryButtons from "./dictionaryButtons";
import {checkMatch, removeQueryStringFromUrl} from "../services/utils";

const GridPage = ({gridName, history, label, isDictionary = false, columns: cols, dictionaryData, preFilters, canEditByRow}) => {
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [height, setHeight] = useState(false);
    const [report, setReport] = useState(null);
    const [options, setOptions] = useState([]);
    const [columns, setColumns] = useState([]);

    const rowCount = 50;
    const [filters, setFilters] = useState([]);
    const [filtersIsLoaded, setFiltersIsLoaded] = useState(false);
    const [order, setOrder] = useState(null);
    const [selectedIds, setSelected] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [settingsForFilters, setSettingsForFilters] = useState({settings: {}});
    const [reportColumns, setReportColumns] = useState([]);

    const columnsFromReport = (report) => {
        return (report ? report.columns : []).map(col => {
            let column = columns.find(c => c.id === col.name) || {};
            return {
                fieldName: column.id,
                type: column.id,
                title: column.displayNameKey,
                disableSort: !sortIsOn(column.id),
                filterType: getFilterTypeByName(column.id),
                canEdit: column.canEdit
            }
        })
    }

    const settingsReport = (report, setSettings = true) => {
        setReport(report);
        if (report && columns) {
            setReportColumns(columnsFromReport(report));
            if (setSettings) {
                const settings = (report.columns || []).map(col => col.settings ? JSON.parse(col.settings) : null) || [];
                const sort = (settings.find(s => s && s.sort) || {}).sort || null;
                setFilters(settings.map(s => s ? s.filter : null).filter(s => s))
                setOrder(sort)
                setFiltersIsLoaded(true);
            }
        }
    }

    useEffect(() => {
        (reportColumns.length && (isDictionary || columns.length)) && loadRowsDebounce(rowCount, 0, getSettings())
    }, [reportColumns, columns, order, filters])

    const getSettings = () => {
        return {
            filters: filters || [],
            order: order || null
        }
    }

    useEffect(() => {
        setSettingsForFilters({settings: getSettings()})
    }, [filters])

    const loadRows = (takeCount, skipCount, settings) => {
        setLoading(true)
        getList(gridName, {
            settings,
            takeCount,
            skipCount
        }, !isDictionary).then(data => {
            let rs;
            if (skipCount === 0) {
                rs = data.entries || [];
                scrollToTop();
            }
            else rs = [...rows, ...(data.entries || [])]
            setRows(rs || []);
            data.totalCount && setTotalCount(data.totalCount);
            setLoading(false);
        }).catch(() => {
            setLoading(false)
            toast.error('Ошибка загрузки')
        })
    }

    const loadRowsDebounce = useCallback(debounce(loadRows, 1000), []);

    const loadNextPart = () => {
        if ((rows.length < totalCount) && !loading) loadRows(rowCount, rows.length, getSettings());
    };

    const loadData = () => {
        if (isDictionary) {
            apiGet(`grid/settings/${gridName}`).then((d) => {
                if (d) {
                    setFilters(d.filters || []);
                    setOrder(d.order);
                    setFiltersIsLoaded(true);
                }
            })
        } else {
            getRepresentations(gridName).then(d => {
                setOptions(d.grids.map(r => ({
                    value: r.id,
                    key: r.id,
                    text: r.name,
                    data: r
                })))
            });
            getColumns(gridName).then(d => {
                setColumns(d.columns.map(c => ({
                    id: c.name,
                    displayNameKey: c.description,
                    canEdit: c.isEditable
                })))
            })
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (cols && cols.length) {
            setReportColumns(cols.filter(col => !col.rolesForView || checkMatch(getRoles(), col.rolesForView)))
        }
    }, [cols]);

    const saveFilters = (report, filters, order) => {
        if (!isDictionary) {
            const form = {
                ...report,
                columns: report.columns.map(col => {
                    let column = {...col};
                    const filter = filters.find(f => f.fieldName === col.name) || null;
                    const sort = (order && (order.fieldName === col.name)) ? order : null;
                    column.settings = (filter || sort) ? JSON.stringify({filter, sort}) : null
                    return column;
                })
            }
            save(form, false);
        } else {
            apiPost(`grid/settings/${gridName}`, {
                filters,
                order
            }).then(() => {})
        }
    }

    const saveFilterDebounce = useCallback(debounce(saveFilters, 2000), []);

    useEffect(() => {
        if (!isDictionary && options && columns.length) {
            const index = options.findIndex(o => report ? (o.value === report.id) : (o.data || {}).isDefault);
            const defaultOptionIndex = index !== -1 ? index : 0;
            settingsReport((options[defaultOptionIndex] || {}).data, !report);
        }
    }, [options, columns])

    const roH = useRef(new ResizeObserver(el => {
        setHeight(el[0].target.clientHeight);
    }));

    const resizeH = useCallback((container) => {
        if (container) roH.current.observe(container);
        else if (roH.current) {
            roH.current.disconnect();
            setHeight(0);
        }
    }, [roH.current]);

    const downloadExcel = () => {
        setLoading(true)
        apiPost(`${gridName}${isDictionary ? '' : '/grid'}/excel`, {
            settings: getSettings(),
            columns: !isDictionary && (reportColumns || []).map(c => c.fieldName),
            gridName: !isDictionary && report.name
        })
            .then(data => {
                setLoading(false)
                window.location.href = getApiBaseUrl() + 'files/' + data.result;
            })
            .catch(err => {
                setLoading(false);
                console.log(err)
                toast.error('Произошла ошибка')
            });
    }

    const save = (form, load = true) => {
        setSaveLoading(true)
        createRepresentations(gridName, form).then(() => {
            setSaveLoading(false)
            load && loadData();
        }).catch(() => {
            setSaveLoading(false)
            toast.error('Произошла ошибка при сохранении отчета')
        })
    }

    const onDelete = (id) => {
        setSaveLoading(true)
        deleteRepresentations(gridName, id).then(() => {
            setSaveLoading(false)
            loadData();
            toast.success('Отчет успешно удален')
        }).catch(() => {
            setSaveLoading(false)
            toast.error('Произошла ошибка при удалении отчета')
        })
    }

    const updateSort = (fieldName) => {
        const oldOrder = (order || {}).fieldName === fieldName ? order.order : null;
        const newOrder = oldOrder === 'desc' ? null : {
            fieldName,
            order: !oldOrder ? 'asc' : 'desc'
        };
        setOrder(newOrder)
        saveFilterDebounce(report, filters, newOrder);
    }

    const newFiltersArrayByFilter = (f, filters) => {
        const filterIndex = filters.findIndex(fl => fl.fieldName === f.fieldName);
        const prevFilters = [...filters];
        filterIndex !== -1 ? prevFilters[filterIndex] = f : prevFilters.push(f)
        return prevFilters.filter(f => f.value);
    }
    const updateFilter = (f, notSave = false, replace = false) => {
        let newFilters = replace ? [] : [...filters];
        if (Array.isArray(f)) {
            f.forEach(fv => {
                newFilters = newFiltersArrayByFilter(fv, newFilters);
            });
        } else newFilters = newFiltersArrayByFilter(f, newFilters);
        setFilters(newFilters);
        !notSave && saveFilterDebounce(report, newFilters, order);
    }

    const setSelectedAllRequest = (isSelect) => {
        if (isSelect) apiPost(`${gridName}${!isDictionary ? '/list/author' : ''}/ids`, {
            settings: getSettings()
        }).then(d => {
            setSelected(d.ids);
        }).catch(e => console.log(e))
        else {
            setSelected([]);
        }
    }

    const ref = useRef();

    const scrollToTop = () => {
        if (ref.current) ref.current.scrollTop = 0;
    }

    useEffect(() => {
        if (filters) {
            scrollToTop();
        }
    }, [filters, order])

    const selectAll = (isSelect) => {
        setSelectedAllRequest(isSelect);
    }

    const roles = getRoles() || [];
    const isCoordinator = roles.includes('Coordinator');
    const isAuthor = roles.includes('Author') || isCoordinator;

    const clearFiltersAndSort = () => {
        preFilters && removeQueryStringFromUrl();
        setFilters([]);
        setOrder(null);
        saveFilterDebounce(report, [], null);
    }
    const setPreFilters = () => {
        //устанавливает фильтры из location.search ВМЕСТО сохраненных фильтров
        //фильтры из location.search НЕ сохраняются у пользователя, если он не поменяет какой-либо фильтр вручную
        updateFilter(Object.keys(preFilters).map(f => ({
            fieldName: f,
            value: preFilters[f],
            operation: 'contains'
        })), true, true)
    }

    useEffect(() => {
        (preFilters && filtersIsLoaded) && setPreFilters();
    }, [preFilters, filtersIsLoaded]);

    const reload = () => loadRows(rowCount, 0, getSettings())

    return (
        <div className='new-styles new-styles__p-0'>
            <div className='grid__header' ref={resizeH}>
                <GridHeader
                    input={
                        !isDictionary && <Representation
                            loading={saveLoading}
                            columns={columns}
                            options={options}
                            value={report}
                            setValue={(v) => {
                                settingsReport(v)
                            }}
                            save={save}
                            onDelete={onDelete}
                        />
                    }
                    loading={loading}
                    name={label}
                    history={history}
                    canCreate={isAuthor && !isDictionary}
                    downloadExcel={downloadExcel}
                    btn={(dictionaryData || {}).createBtnText && <Button
                        className='btn_light-blue btn_icon_plus'
                        onClick={() => history.push(`/dictionary/${gridName}/new`)}
                    >
                        {dictionaryData.createBtnText}
                    </Button>}
                />
                {!isDictionary && <ParcelsButtons
                    selectedItems={selectedIds}
                    reload={reload}
                    setLoading={setLoading}
                    loading={loading}
                    history={history}
                    clearDisabled={!((filters || []).length || order)}
                    clearFilters={clearFiltersAndSort}
                />}
                {isDictionary && <DictionaryButtons
                    dictionaryData={dictionaryData}
                    selectedItems={selectedIds}
                    reload={() => loadRowsDebounce(rowCount, 0, getSettings())}
                    setLoading={setLoading}
                    loading={loading}
                    history={history}
                    clearDisabled={!((filters || []).length || order)}
                    clearFilters={clearFiltersAndSort}
                />}
            </div>
            <Segment vertical>
                <Dimmer className='grid__dimmer' active={loading} inverted>
                    <Loader label={'Загрузка'}/>
                </Dimmer>
                <div
                    className='grid__tbl scroll-style'
                    style={{
                        height: `calc(100vh - ${height}px)`
                    }}
                    ref={ref}
                >
                    <TableGrid
                        gridName={gridName}
                        isDictionary={isDictionary}
                        isGrid
                        selectedIds={selectedIds}
                        setAll={selectAll}
                        setSelected={(id, value) => {
                            setSelected(ids => {
                                value ? ids = [...(ids || []), id] : ids = ids.filter(i => i !== id);
                                return ids;
                            })
                        }}
                        totalCount={totalCount}
                        rowCount={rowCount}
                        columns={reportColumns}
                        checkbox
                        updateFilter={updateFilter}
                        filters={filters}
                        rows={rows}
                        setSort={updateSort}
                        sort={order}
                        loading={loading}
                        infinityScroll
                        striped={false}
                        options={settingsForFilters}
                        loadNextPart={loadNextPart}
                        history={history}
                        reload={reload}
                        canEditByRow={canEditByRow}
                    />
                </div>
            </Segment>
        </div>
    );
};

export default GridPage;


// WEBPACK FOOTER //
// ./App/grids/gridPage.jsx